import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import Decimal from "decimal.js";

const state = {
    providers: [],
    header_providers: [],
    months_coverage: 2,
    history_months: 12,
    demand_basis: "sales",
    has_provider_products: null,
    provider_id: null,
    active_sales_only: false,
    purchase_mode: "coverage",
    sale_product_tags: [],
    ordering: "provider",
    loading: false,
    fetch_sequence: 0,
    loading_errors: null,
    creating_order: false,
    has_loaded: false,
    archiving: false,
};

const mutations = {
    restoreEmptyPurchasePrice(state, { product }) {
        if (String(product.price_input ?? '').trim() !== '' || product.original_purchase_price == null) return;
        product.unit_purchase_price_excl_vat = Number(product.original_purchase_price);
        product.price_input = String(product.unit_purchase_price_excl_vat);
    },
    updatePurchasePrice(state, { product, value }) {
        product.price_input = value;
        const normalized = String(value ?? '').trim().replace(',', '.');
        const number = normalized === '' ? NaN : Number(normalized);
        product.unit_purchase_price_excl_vat = Number.isFinite(number) && number >= 0 ? number : null;
    },
    updateProviders(state, providers) {
        state.providers = providers || [];
    },
    updateHeaderProviders(state, providers) {
        state.header_providers = providers || [];
    },
    updateHistoryMonths(state, months) { state.history_months = months; },
    updateMonthsCoverage(state, months) {
        state.months_coverage = months;
    },
    updateDemandBasis(state, basis) {
        state.demand_basis = basis || "sales";
    },
    updateHasProviderProducts(state, hasProviderProducts) {
        state.has_provider_products = hasProviderProducts;
    },
    updateProviderId(state, providerId) {
        state.provider_id = providerId || null;
    },
    updateActiveSalesOnly(state, enabled) {
        state.active_sales_only = !!enabled;
        state.purchase_mode = "coverage";
    },
    updatePurchaseMode(state, mode) {
        state.purchase_mode = state.active_sales_only && mode === "sales" ? "sales" : "coverage";
    },
    updateSaleProductTags(state, tags) {
        state.sale_product_tags = tags || [];
    },
    updateOrdering(state, ordering) {
        state.ordering = ordering === "amount" ? "amount" : "provider";
    },
    advanceFetchSequence(state) {
        state.fetch_sequence += 1;
    },
    updateLoading(state, loading) {
        state.loading = loading;
    },
    updateLoadingErrors(state, errors) {
        state.loading_errors = errors;
    },
    updateCreatingOrder(state, creating) {
        state.creating_order = creating;
    },
    updateHasLoaded(state, hasLoaded) {
        state.has_loaded = hasLoaded;
    },
    updateArchiving(state, archiving) {
        state.archiving = archiving;
    },
    toggleProduct(state, { providerIndex, productIndex }) {
        if (state.providers[providerIndex] && state.providers[providerIndex].products[productIndex]) {
            state.providers[providerIndex].products[productIndex].selected =
                !state.providers[providerIndex].products[productIndex].selected;
        }
    },
    updateProductQuantity(state, { providerIndex, productIndex, quantity }) {
        if (state.providers[providerIndex] && state.providers[providerIndex].products[productIndex]) {
            state.providers[providerIndex].products[productIndex].edited_quantity = quantity;
        }
    },
    removeProduct(state, { providerIndex, productIndex }) {
        const provider = state.providers[providerIndex];
        if (!provider) {
            return;
        }
        provider.products.splice(productIndex, 1);
        if (!provider.products.length) {
            state.providers.splice(providerIndex, 1);
        }
    },
};

const actions = {
    async fetchHeaderProviders({ commit }) {
        try {
            const response = await axios.get("/api/providers/", {
                params: { archived: false, ordering: "name", limit: 500 },
            });
            commit("updateHeaderProviders", response.data.results || []);
        } catch (xhr_error) {
            commit("updateHeaderProviders", []);
            throw utils.handleError(xhr_error);
        }
    },

    async fetchToOrder({ commit, state }) {
        commit("advanceFetchSequence");
        const sequence = state.fetch_sequence;
        commit("updateLoading", true);
        commit("updateLoadingErrors", null);
        try {
            const params = {
                demand_basis: state.demand_basis || "sales",
            };
            if (state.active_sales_only && state.purchase_mode === "sales") {
                params.purchase_mode = "sales";
            } else {
                params.months_coverage = state.months_coverage;
                params.history_months = state.history_months;
            }
            if (state.provider_id) {
                params.provider = state.provider_id;
            }
            if (state.active_sales_only) {
                params.active_sales_only = true;
            }
            if (state.sale_product_tags.length) {
                params.sale_product_tags = state.sale_product_tags.map(tag => typeof tag === "string" ? tag : tag.name).join(",");
            }
            const response = await axios.get("/api/shop/to-order/", { params });
            if (sequence !== state.fetch_sequence) return;
            const providers = response.data.providers || [];
            providers.forEach((provider) => {
                (provider.products || []).forEach((product) => {
                    const qty = Math.ceil(parseFloat(product.quantity_to_order) || 0);
                    product.quantity_to_order = qty;
                    product.selected = qty > 0;
                    product.edited_quantity = null;
                    product.price_input = product.unit_purchase_price_excl_vat == null ? '' : String(Number(product.unit_purchase_price_excl_vat));
                    product.original_purchase_price = product.unit_purchase_price_excl_vat;
                });
            });
            commit("updateProviders", providers);
            commit(
                "updateHasProviderProducts",
                Object.prototype.hasOwnProperty.call(response.data, "has_provider_products")
                    ? !!response.data.has_provider_products
                    : null,
            );
            commit("updateHasLoaded", true);
            return response.data;
        } catch (xhr_error) {
            if (sequence !== state.fetch_sequence) return;
            const error = utils.handleError(xhr_error);
            commit("updateLoadingErrors", error);
            commit("updateProviders", []);
            commit("updateHasProviderProducts", false);
            throw error;
        } finally {
            if (sequence === state.fetch_sequence) commit("updateLoading", false);
        }
    },

    async changeHistoryMonths({ commit, dispatch, state }, months) {
        commit("updateHistoryMonths", months);
        if (state.has_loaded) await dispatch("fetchToOrder");
    },

    async changeMonthsCoverage({ commit, dispatch, state }, months) {
        commit("updateMonthsCoverage", months);
        if (state.has_loaded) {
            await dispatch("fetchToOrder");
        }
    },

    async changeDemandBasis({ commit, dispatch, state }, basis) {
        commit("updateDemandBasis", basis);
        if (state.has_loaded) {
            await dispatch("fetchToOrder");
        }
    },

    async changeProvider({ commit, dispatch, state }, providerId) {
        commit("updateProviderId", providerId);
        if (state.has_loaded) {
            await dispatch("fetchToOrder");
        }
    },

    async changeActiveSalesOnly({ commit, dispatch, state }, enabled) {
        commit("updateActiveSalesOnly", enabled);
        if (state.has_loaded) {
            await dispatch("fetchToOrder");
        }
    },

    async changeSaleProductTags({ commit, dispatch, state }, tags) {
        commit("updateSaleProductTags", tags);
        if (state.has_loaded) await dispatch("fetchToOrder");
    },

    async changePurchaseMode({ commit, dispatch, state }, mode) {
        commit("updatePurchaseMode", mode);
        if (state.has_loaded) await dispatch("fetchToOrder");
    },

    toggleProduct({ commit, state }, { provider, product }) {
        const providerIndex = state.providers.findIndex(
            (row) => (row.provider || row.provider_id) === (provider.provider || provider.provider_id),
        );
        if (providerIndex === -1) {
            return;
        }
        const productIndex = state.providers[providerIndex].products.findIndex(
            (row) => (row.product || row.product_id) === (product.product || product.product_id),
        );
        if (productIndex !== -1) {
            commit("toggleProduct", { providerIndex, productIndex });
        }
    },

    toggleAllProducts({ state }, { provider, selected }) {
        const providerIndex = state.providers.findIndex(
            (row) => (row.provider || row.provider_id) === (provider.provider || provider.provider_id),
        );
        if (providerIndex === -1) {
            return;
        }
        state.providers[providerIndex].products.forEach((product) => {
            product.selected = selected;
        });
    },

    updateQuantity({ commit, state }, { provider, product, quantity }) {
        const providerIndex = state.providers.findIndex(
            (row) => (row.provider || row.provider_id) === (provider.provider || provider.provider_id),
        );
        if (providerIndex === -1) {
            return;
        }
        const productIndex = state.providers[providerIndex].products.findIndex(
            (row) => (row.product || row.product_id) === (product.product || product.product_id),
        );
        if (productIndex === -1) {
            return;
        }
        const numQuantity = Math.ceil(parseFloat(quantity));
        if (!isNaN(numQuantity) && numQuantity >= 0) {
            commit("updateProductQuantity", { providerIndex, productIndex, quantity: numQuantity });
        }
    },

    increaseQuantity({ dispatch, state }, { provider, product }) {
        const currentQuantity =
            product.edited_quantity !== null && product.edited_quantity !== undefined
                ? product.edited_quantity
                : product.quantity_to_order;
        dispatch("updateQuantity", {
            provider,
            product,
            quantity: parseFloat(currentQuantity) + 1,
        });
    },

    decreaseQuantity({ dispatch }, { provider, product }) {
        const currentQuantity =
            product.edited_quantity !== null && product.edited_quantity !== undefined
                ? product.edited_quantity
                : product.quantity_to_order;
        dispatch("updateQuantity", {
            provider,
            product,
            quantity: Math.max(0, parseFloat(currentQuantity) - 1),
        });
    },

    findProductIndexes({ state }, { provider, product }) {
        const providerIndex = state.providers.findIndex(
            (row) => (row.provider || row.provider_id) === (provider.provider || provider.provider_id),
        );
        if (providerIndex === -1) {
            return { providerIndex: -1, productIndex: -1 };
        }
        const productIndex = state.providers[providerIndex].products.findIndex(
            (row) => (row.product || row.product_id) === (product.product || product.product_id),
        );
        return { providerIndex, productIndex };
    },

    async archiveProduct({ commit, dispatch }, { provider, product }) {
        const productId = product.product || product.product_id || product.product_data?.id;
        if (!productId) {
            return;
        }
        const { providerIndex, productIndex } = await dispatch("findProductIndexes", {
            provider,
            product,
        });
        if (providerIndex === -1 || productIndex === -1) {
            return;
        }
        commit("updateArchiving", true);
        try {
            await axios.post(`/api/providerproducts/${productId}/archive/`);
            commit("removeProduct", { providerIndex, productIndex });
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateArchiving", false);
        }
    },

    async createOrderForProvider({ commit }, { provider, stockId, saveChangedPrices = false }) {
        commit("updateCreatingOrder", true);
        try {
            const selectedItems = (provider.products || [])
                .filter((product) => product.selected)
                .map((product) => {
                    const quantity =
                        product.edited_quantity !== null && product.edited_quantity !== undefined
                            ? product.edited_quantity
                            : product.quantity_to_order;
                    return {
                        catalogentry: product.catalogentry || product.catalogentry_id,
                        quantity: Math.ceil(parseFloat(quantity) || 0),
                        unit_purchase_price_excl_vat: product.unit_purchase_price_excl_vat,
                        save_purchase_price: Boolean(saveChangedPrices && hasChangedPurchasePrice(product)),
                    };
                })
                .filter((item) => item.quantity > 0 && item.catalogentry);

            if (selectedItems.length === 0) {
                throw new Error("No products selected");
            }
            if (selectedItems.some(item => item.unit_purchase_price_excl_vat == null)) {
                throw new Error("Renseignez le prix d’achat HT de chaque produit sélectionné.");
            }
            if (!stockId) {
                throw new Error("No warehouse");
            }

            const providerId = provider.provider || provider.provider_id;
            const response = await axios.post(`/api/providers/${providerId}/create-order/`, {
                stock: stockId,
                items: selectedItems,
            });
            return response.data;
        } catch (xhr_error) {
            if (xhr_error.response) {
                throw utils.handleError(xhr_error);
            }
            throw xhr_error;
        } finally {
            commit("updateCreatingOrder", false);
        }
    },
};

const selectedRows = provider => (provider.products || []).filter(product =>
    product.selected && Number(product.edited_quantity ?? product.quantity_to_order) > 0);
const providerAmount = provider => selectedRows(provider).reduce((total, product) => total.plus(
    new Decimal(product.unit_purchase_price_excl_vat || 0).times(product.edited_quantity ?? product.quantity_to_order),
), new Decimal(0));

const getters = {
    orderedProviders(state, getters, rootState) {
        const locale = rootState.session?.active_locale || "fr";
        return [...state.providers].sort((a, b) => {
            if (state.ordering === "amount") {
                const difference = providerAmount(b).comparedTo(providerAmount(a));
                if (difference) return difference;
            }
            return (a.provider_data?.name || "").localeCompare(b.provider_data?.name || "", locale, { sensitivity: "base" });
        });
    },
    selectionSummary(state) {
        const providers = new Set();
        const products = new Set();
        let amount = new Decimal(0);
        state.providers.forEach(provider => {
            const rows = selectedRows(provider);
            if (rows.length) providers.add(provider.provider || provider.provider_id);
            rows.forEach(product => products.add(product.product || product.product_id || product.product_data?.id));
            amount = amount.plus(providerAmount(provider));
        });
        return { providers: providers.size, products: products.size, amount: amount.toNumber() };
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};


export function hasChangedPurchasePrice(product) {
    return Boolean(product.can_save_purchase_price && product.selected
        && Number(product.edited_quantity ?? product.quantity_to_order) > 0
        && product.unit_purchase_price_excl_vat != null
        && (product.original_purchase_price == null
            || !new Decimal(product.unit_purchase_price_excl_vat).equals(product.original_purchase_price)));
}
